"use client";
import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import maplibregl, { Map as MlMap, Marker } from "maplibre-gl";
import { Icons } from "@/components/Icons";
import { Button, Card, StickyBar, TopBar } from "@/components/ui";
import { FormField } from "@/components/FormField";
import { useAddresses } from "@/hooks/useAddresses";
import { useAuth } from "@/store/auth";
import { useBranch } from "@/store/branch";
import { useFulfillment } from "@/store/fulfillment";
import { scopedStorageKey } from "@/lib/storageScope";
import {
  DEFAULT_CENTER,
  DEFAULT_MAP_STYLE,
  DEFAULT_ZOOM,
  normalizeCountryCode,
  resolveCountryCode,
  type ResolvedAddress,
} from "@/lib/location";
import type {
  AddressCreate,
  AddressUpdate,
} from "@/api/online-ordering-service/model";
import type { GeoPoint } from "@/api/online-ordering-service/model/geoPoint";

const TEMP_ADDRESS_KEY = "v2_temp_selected_address";

type FormState = {
  addressLabel: string;
  buildingName: string;
  streetName: string;
  aptNo: string;
  floor: string;
  name: string;
  additionalDirections: string;
};

const EMPTY_FORM: FormState = {
  addressLabel: "",
  buildingName: "",
  streetName: "",
  aptNo: "",
  floor: "",
  name: "",
  additionalDirections: "",
};

// v1 packs apt / floor / directions into the `street2` slot using a deterministic
// "Apt: X, Floor: Y, Directions: Z" format because the SDK's AddressCreate doesn't
// expose dedicated fields for them. We follow the same convention here so v1 and
// v2 addresses round-trip cleanly.
function combineStreet2(aptNo: string, floor: string, directions: string): string {
  let combined = "";
  if (aptNo) combined += `Apt: ${aptNo}`;
  if (floor) combined += `${combined ? ", " : ""}Floor: ${floor}`;
  if (directions) combined += `${combined ? ", " : ""}Directions: ${directions}`;
  return combined;
}

function parseStreet2(street2: string): {
  buildingName: string;
  aptNo: string;
  floor: string;
  additionalDirections: string;
} {
  const result = { buildingName: "", aptNo: "", floor: "", additionalDirections: "" };
  const buildingParts: string[] = [];
  for (const raw of street2.split(",")) {
    const part = raw.trim();
    if (part.startsWith("Apt:")) result.aptNo = part.substring(4).trim();
    else if (part.startsWith("Floor:")) result.floor = part.substring(6).trim();
    else if (part.startsWith("Directions:")) result.additionalDirections = part.substring(11).trim();
    else if (part) buildingParts.push(part);
  }
  result.buildingName = buildingParts.join(", ");
  return result;
}

function inferCityCountry(label: string): { city: string; country: string } {
  const parts = label
    .split(/[,\u060C]/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) return { city: "", country: "" };
  const looksLikeCoordinates = parts.every((part) => /^-?\d+(\.\d+)?$/.test(part));
  if (looksLikeCoordinates) return { city: "", country: "" };
  return {
    city: parts[parts.length - 2] ?? "",
    country: normalizeCountryCode(parts[parts.length - 1]),
  };
}

function readTempAddress(storageKey: string): ResolvedAddress | null {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ResolvedAddress;
    return parsed && typeof parsed.lat === "number" && typeof parsed.lng === "number" ? parsed : null;
  } catch {
    return null;
  }
}

function clearTempAddress(storageKey: string) {
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    // ignore
  }
}

export function NewAddressScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { addressId } = useParams<{ addressId: string }>();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id") ?? addressId ?? null;
  const returnTo = (searchParams.get("returnTo") as string | null) ?? "addresses";

  const { user } = useAuth();
  const { branchSlug, branchConfig } = useBranch();
  const { addresses, loading, createAddress, updateAddress, getAddress } = useAddresses();
  const { setMode, setSelectedAddressId, manualAddress } = useFulfillment();
  const tempAddressKey = scopedStorageKey(TEMP_ADDRESS_KEY, branchSlug);

  // Pull a freshly-picked address from navigation state OR sessionStorage
  // (the latter survives auth redirects, mirroring v1's `temp_selected_address`).
  const stateAddr = (location.state as { picked?: ResolvedAddress } | null)?.picked ?? null;
  const initialManual = useMemo<ResolvedAddress | null>(() => {
    if (!manualAddress) return null;
    return {
      lat: manualAddress.coordinates?.latitude ?? 0,
      lng: manualAddress.coordinates?.longitude ?? 0,
      street: manualAddress.street || "",
      city: manualAddress.city || "",
      state: manualAddress.state || "",
      postalCode: manualAddress.postalCode || "",
      country: manualAddress.country || "",
      label: manualAddress.name || "",
    };
  }, [manualAddress]);

  const initialPicked = stateAddr ?? (!editId ? readTempAddress(tempAddressKey) || initialManual : null);

  const [form, setForm] = useState<FormState>({
    ...EMPTY_FORM,
    name: user?.name ?? "",
  });
  const [coords, setCoords] = useState<GeoPoint | null>(
    initialPicked
      ? { latitude: initialPicked.lat, longitude: initialPicked.lng }
      : null,
  );
  const [areaSummary, setAreaSummary] = useState<string>(
    initialPicked?.label || initialPicked?.street || "",
  );
  // v1 stores city/country from the geocode result and resends them on save.
  const [geoCity, setGeoCity] = useState<string>(initialPicked?.city ?? "");
  const [geoCountry, setGeoCountry] = useState<string>(
    normalizeCountryCode(initialPicked?.country),
  );
  const [geoState, setGeoState] = useState<string>(initialPicked?.state ?? "");
  const [geoPostal, setGeoPostal] = useState<string>(initialPicked?.postalCode ?? "");
  const [hydrated, setHydrated] = useState<boolean>(!editId);
  const [submittedOnce, setSubmittedOnce] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingAddr, setLoadingAddr] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill from picked map address (create flow).
  useEffect(() => {
    if (!initialPicked || editId) return;
    setForm((prev) => ({
      ...prev,
      streetName: prev.streetName || initialPicked.street,
      addressLabel: prev.addressLabel || initialPicked.label || "",
    }));
    // Consume sessionStorage entry so a later create doesn't reuse stale data.
    clearTempAddress(tempAddressKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Editing flow can return from the map picker with a new pin while keeping
  // existing form fields intact.
  useEffect(() => {
    if (!editId || !stateAddr) return;
    setCoords({ latitude: stateAddr.lat, longitude: stateAddr.lng });
    setAreaSummary(stateAddr.label || stateAddr.street || "");
    setGeoCity(stateAddr.city ?? "");
    setGeoCountry(normalizeCountryCode(stateAddr.country));
    setGeoState(stateAddr.state ?? "");
    setGeoPostal(stateAddr.postalCode ?? "");
    setForm((prev) => ({
      ...prev,
      streetName: stateAddr.street || prev.streetName,
    }));
  }, [editId, stateAddr]);

  // Fetch address details when editing (ensures fresh data).
  useEffect(() => {
    if (!editId || hydrated) return;

    setLoadingAddr(true);
    getAddress(editId)
      .then((addr) => {
        const parsed = parseStreet2(addr.street2 ?? "");
        setForm({
          addressLabel: addr.name ?? "",
          buildingName: parsed.buildingName,
          streetName: addr.street ?? "",
          aptNo: parsed.aptNo,
          floor: parsed.floor,
          name: user?.name ?? "",
          additionalDirections: parsed.additionalDirections,
        });
        setGeoCity(addr.city ?? "");
        setGeoCountry(normalizeCountryCode(addr.country));
        setGeoState(addr.state ?? "");
        setGeoPostal(addr.postalCode ?? "");
        if (addr.coordinates) {
          setCoords({
            latitude: addr.coordinates.latitude,
            longitude: addr.coordinates.longitude,
          });
          setAreaSummary([addr.street, addr.city, addr.state].filter(Boolean).join(", "));
        }
        setHydrated(true);
      })
      .catch((err) => {
        console.warn("[NewAddress] fetch failed", err);
        setError(err instanceof Error ? err.message : t("v2.address.err_load"));
        setHydrated(true);
      })
      .finally(() => {
        setLoadingAddr(false);
      });
  }, [editId, getAddress, hydrated, user?.name, t]);

  // If we have neither a picked address nor an edit target, redirect to map picker (v1 behaviour).
  useEffect(() => {
    if (editId) return;
    if (initialPicked) return;
    if (!stateAddr && !readTempAddress(tempAddressKey)) {
      const params = new URLSearchParams({ returnTo: "new-address-form" });
      if (returnTo && returnTo !== "addresses") params.set("next", returnTo);
      navigate(`/specify-location?${params.toString()}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k: keyof FormState, v: string) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const isEditing = Boolean(editId);

  const valid =
    form.addressLabel.trim().length > 0 &&
    form.streetName.trim().length > 0 &&
    form.name.trim().length > 0;

  async function handleSave() {
    setError(null);
    setSubmittedOnce(true);
    if (!valid || saving) return;
    setSaving(true);
    try {
      const street2Combined = combineStreet2(
        form.aptNo,
        form.floor,
        form.additionalDirections,
      );
      const buildingPrefix = form.buildingName.trim();
      // v1 squashes building name into street2 prefix when present.
      const street2Final = buildingPrefix
        ? street2Combined
          ? `${buildingPrefix}, ${street2Combined}`
          : buildingPrefix
        : street2Combined;
      const resolved: GeoPoint | null = coords;
      const inferred = inferCityCountry(areaSummary || form.streetName);
      // The backend requires a 2-letter ISO country code (min_length=2). Geocode
      // results occasionally omit it, which previously sent an empty string and
      // made the create/update address call 500 — blocking checkout. Fall back
      // to the branch's own country, then to EG, so we never send an empty code.
      const branchAddress = branchConfig?.branches?.[0]?.address as
        | { country?: string; city?: string }
        | null
        | undefined;
      const branchCountry = branchAddress?.country;
      const branchCity = branchAddress?.city;
      const country = resolveCountryCode([
        geoCountry,
        inferred.country,
        branchCountry,
      ]);
      const finalCity =
        geoCity.trim() ||
        inferred.city.trim() ||
        geoState.trim() ||
        branchCity?.trim() ||
        form.streetName.trim() ||
        "City";

      if (editId) {
        const patch: AddressUpdate = {
          name: form.addressLabel,
          street: form.streetName,
          street2: street2Final || null,
          city: finalCity,
          state: geoState || null,
          postalCode: geoPostal || null,
          country: country,
          coordinates: resolved,
        };
        const updated = await updateAddress(editId, patch);
        setMode("delivery");
        setSelectedAddressId(updated.id);
      } else {
        const create: AddressCreate = {
          name: form.addressLabel,
          street: form.streetName,
          street2: street2Final || null,
          city: finalCity,
          state: geoState || null,
          postalCode: geoPostal || null,
          country,
          coordinates: resolved,
        };
        const created = await createAddress(create);
        setMode("delivery");
        setSelectedAddressId(created.id);
      }
      // Mirror v1's navigateAfterSave.
      if (returnTo === "checkout") navigate("/checkout");
      else if (returnTo === "home") navigate("/");
      else navigate("/addresses");
    } catch (err) {
      console.warn("[NewAddress] save failed", err);
      setError(err instanceof Error ? err.message : t("v2.address.err_save"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ paddingBottom: 140 }}>
      {loadingAddr ? (
        <>
          <TopBar title={t("v2.address.title_edit")} onBack={() => navigate(-1)} />
          <div style={{ padding: 80, textAlign: "center", color: "var(--ink-3)" }}>
            <div style={{ marginBottom: 12 }}>
              <Icons.loading size={32} className="animate-spin" />
            </div>
            {t("v2.common.loading")}
          </div>
        </>
      ) : (
        <>
          <TopBar
            title={isEditing ? t("v2.address.title_edit") : t("v2.address.title_new")}
            onBack={() => navigate(-1)}
          />

          {/* Area summary card (v1 pattern: small header before form, not a full map). */}
          <div style={{ padding: "8px 16px 12px" }}>
            <Card padding={12}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Icons.pin size={16} style={{ color: "var(--ink-1)" }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-1)" }}>
                  {t("v2.address.area")}
                </div>
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--ink-2)",
                  marginTop: 4,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {areaSummary || form.streetName || t("v2.address.area_unset")}
              </div>
            </Card>
          </div>

          <div style={{ padding: "0 16px 12px" }}>
            <Card padding={0} style={{ overflow: "hidden" }}>
              <div style={{ height: 120, position: "relative" }}>
                <MiniMap coords={coords} />
                <button
                  onClick={() => {
                    const params = new URLSearchParams({ returnTo: "new-address-form" });
                    if (editId) params.set("id", editId);
                    navigate(`/specify-location?${params.toString()}`);
                  }}
                  style={{
                    position: "absolute",
                    insetInlineEnd: 12,
                    bottom: 12,
                    padding: "6px 12px",
                    borderRadius: "var(--radius-chip)",
                    fontSize: 12,
                    fontWeight: 600,
                    background: "var(--surface-1)",
                    border: "1px solid var(--border)",
                    color: "var(--ink-1)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    zIndex: 5,
                  }}
                >
                  <Icons.edit size={12} /> {t("v2.address.adjust_pin")}
                </button>
              </div>
            </Card>
          </div>

          <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 14 }}>
            <FormField
              label={t("v2.address.address_label")}
              value={form.addressLabel}
              onChange={(v) => update("addressLabel", v)}
              placeholder={t("v2.address.address_label_placeholder")}
              required
            />
            {submittedOnce && !form.addressLabel.trim() && (
              <div style={errStyle}>{t("v2.address.address_required")}</div>
            )}

            <FormField
              label={t("v2.address.building_name")}
              value={form.buildingName}
              onChange={(v) => update("buildingName", v)}
              placeholder={t("v2.address.building_name_placeholder")}
            />

            <FormField
              label={t("v2.address.street_name")}
              value={form.streetName}
              onChange={(v) => update("streetName", v)}
              placeholder={t("v2.address.street_name_placeholder")}
              required
            />
            {submittedOnce && !form.streetName.trim() && (
              <div style={errStyle}>{t("v2.address.street_required")}</div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <FormField
                  label={t("v2.address.apt_no")}
                  value={form.aptNo}
                  onChange={(v) => update("aptNo", v)}
                  placeholder={t("v2.address.apt_no_placeholder")}
                  compact
                />
              </div>
              <div style={{ minWidth: 0 }}>
                <FormField
                  label={t("v2.address.floor")}
                  value={form.floor}
                  onChange={(v) => update("floor", v)}
                  placeholder={t("v2.address.floor_placeholder")}
                  compact
                />
              </div>
            </div>

            <FormField
              label={t("v2.address.contact_name")}
              value={form.name}
              onChange={(v) => update("name", v)}
              placeholder={t("v2.address.contact_name_placeholder")}
              required
            />
            {submittedOnce && !form.name.trim() && (
              <div style={errStyle}>{t("v2.address.name_required")}</div>
            )}

            <FormField
              label={t("v2.address.additional_directions")}
              value={form.additionalDirections}
              onChange={(v) => update("additionalDirections", v)}
              placeholder={t("v2.address.additional_directions_placeholder")}
              textarea
            />

            {error && (
              <div
                role="alert"
                style={{
                  fontSize: 13,
                  color: "#b91c1c",
                  padding: "10px 12px",
                  background: "#fee2e2",
                  borderRadius: 10,
                }}
              >
                {error}
              </div>
            )}
          </div>

          <StickyBar
            style={{ bottom: 0, paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
          >
            <Button
              full
              size="lg"
              onClick={handleSave}
              disabled={saving || (submittedOnce && !valid)}
            >
              {saving
                ? t("v2.common.saving")
                : isEditing
                ? t("v2.address.edit_address_btn")
                : t("v2.address.save_address")}
            </Button>
          </StickyBar>
        </>
      )}
    </div>
  );
}

const errStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#b91c1c",
  marginTop: -8,
  paddingLeft: 4,
};

function MiniMap({ coords }: { coords: GeoPoint | null }) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const markerRef = useRef<Marker | null>(null);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const center: [number, number] = coords
      ? [coords.longitude, coords.latitude]
      : DEFAULT_CENTER;
    const map = new maplibregl.Map({
      container: ref.current,
      style: DEFAULT_MAP_STYLE,
      center,
      zoom: coords ? 15 : DEFAULT_ZOOM,
      interactive: false,
      attributionControl: false,
    });
    mapRef.current = map;
    if (coords) {
      markerRef.current = new maplibregl.Marker({ color: "#16a34a" })
        .setLngLat(center)
        .addTo(map);
    }
    return () => {
      markerRef.current?.remove();
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!coords) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }
    const lngLat: [number, number] = [coords.longitude, coords.latitude];
    map.flyTo({ center: lngLat, zoom: 15, animate: false });
    if (markerRef.current) {
      markerRef.current.setLngLat(lngLat);
    } else {
      markerRef.current = new maplibregl.Marker({ color: "#16a34a" })
        .setLngLat(lngLat)
        .addTo(map);
    }
  }, [coords]);

  return <div ref={ref} style={{ position: "absolute", inset: 0 }} />;
}
